import React from "react";
import { CardHeader, CardBody, Row, Col, FormGroup, Label, Input, Button, Alert } from "reactstrap";
import { withTranslation } from "react-i18next";
import withRouter from "../../../../../components/Common/withRouter";

const DeductionForm = ({
  allDeductions,
  deductionsByCategory,
  selectedDeduction,
  setSelectedDeduction,
  customAmount,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  handleSave,
  currentDeduction,
  isSaving,
  backendError,
  isDeductionAlreadyAssigned,
  t
}) => {

  const selectedDeductionObj = allDeductions.find(d => d.id === selectedDeduction);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Check if dates are valid
  const isStartDateValid = startDate && startDate >= getTodayDate();
  const isEndDateValid = !endDate || (endDate && startDate && endDate >= startDate);
  const areDatesValid = isStartDateValid && isEndDateValid;

  // Can save if we have a current deduction ready
  const canSave = currentDeduction && areDatesValid && !isSaving;

  // Handle deduction selection avec vérification
  const handleDeductionChange = (e) => {
    const deductionId = e.target.value;
    
    // Double vérification côté UI
    if (deductionId && isDeductionAlreadyAssigned && isDeductionAlreadyAssigned(deductionId)) {
      // Cette vérification est maintenant gérée dans le hook, mais on garde pour la sécurité
      return;
    }
    
    setSelectedDeduction(deductionId);
  };

  // Compter les déductions disponibles
  const totalAvailableDeductions = Object.values(deductionsByCategory)
    .reduce((total, deductions) => total + deductions.length, 0);

  return (
    <div className="card border-0 row p-2 mt-4">
      <CardHeader className="bg-white border-0 pb-0">
        <h6 className="mb-0">
          <i className="fa fa-list-alt me-2 text-primary"></i>
          {t("assign_deductions")}
        </h6>
      </CardHeader>
      <CardBody>
        {/* Alerte si aucune déduction disponible */}
        {totalAvailableDeductions === 0 && (
          <Alert color="warning" className="mb-4">
            <i className="fa fa-exclamation-triangle me-2"></i>
            {t("no_deductions_available")}
          </Alert>
        )}

        {/* Formulaire - seulement si des déductions sont disponibles */}
        {totalAvailableDeductions > 0 && (
          <>
            <Row className="mb-4">
              {/* Select deduction */}
              <Col xl={5} lg={6} md={12} className="mb-2">
                <FormGroup>
                  <Label className="form-label fw-semibold">
                    {t("select_deduction")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={selectedDeduction}
                    onChange={handleDeductionChange}
                    className="form-select custom-border"
                    disabled={isSaving}
                    required
                  >
                    <option value="">{t("select_deduction")}...</option>
                    {Object.entries(deductionsByCategory).map(([categoryName, deductions]) => (
                      <optgroup key={categoryName} label={categoryName}>
                        {deductions.map(d => (
                          <option key={d.id} value={d.id}>
                            {d.name} ({d.deductionType === "PERCENTAGE" ? "%" : "Fixe"})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {/* Custom amount */}
              {selectedDeduction && (
                <Col xl={3} lg={4} md={6} className="mb-2">
                  <FormGroup>
                    <Label className="form-label fw-semibold">
                      {selectedDeductionObj?.deductionType === "PERCENTAGE" ?  `${t("Rate")}` + " (%)" : `${t("Amount")} ${t(" (XOF)")}`}
                    </Label>
                    <Input 
                      type="number" 
                      value={customAmount} 
                      readOnly 
                      className="custom-border bg-light" 
                    />
                  </FormGroup>
                </Col>
              )}

              {/* Start and end dates */}
              {selectedDeduction && (
                <>
                  <Col xl={2} lg={3} md={6} className="mb-2">
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        {t("start_date")} <span className="text-danger">*</span>
                      </Label>
                      <Input 
                        type="date" 
                        value={startDate} 
                        onChange={(e) => setStartDate(e.target.value)}
                        min={getTodayDate()}
                        className={`custom-border ${startDate && !isStartDateValid ? 'is-invalid' : ''}`}
                        required
                        disabled={isSaving}
                      />
                      {startDate && !isStartDateValid && (
                        <div className="invalid-feedback d-block">
                          {t("start_date_must_be_equal_or_greater_than_today")}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col xl={2} lg={3} md={6} className="mb-2">
                    <FormGroup>
                      <Label className="form-label fw-semibold">{t("end_date")}</Label>
                      <Input 
                        type="date" 
                        value={endDate} 
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate || getTodayDate()}
                        className={`custom-border ${endDate && !isEndDateValid ? 'is-invalid' : ''}`}
                        disabled={!startDate || isSaving}
                      />
                      {endDate && !isEndDateValid && (
                        <div className="invalid-feedback d-block">
                          {t("end_date_must_be_equal_or_greater_than_start_date")}
                        </div>
                      )}
                     
                    </FormGroup>
                  </Col>
                </>
              )}
            </Row>

            {/* Erreur spécifique au formulaire */}
            {/* {backendError && selectedDeduction && (
              <Row className="mb-3">
                <Col md={12}>
                  <Alert color="danger" className="mb-0">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    <strong>Erreur de validation :</strong> {backendError}
                  </Alert>
                </Col>
              </Row>
            )} */}

            {/* Save Button */}
            <Row className="mb-4">
              <Col xl={3} lg={4} md={6} className="mb-2">
                <Button 
                  color="success" 
                  onClick={handleSave} 
                  disabled={!canSave} 
                  className="w-100"
                  size="md"
                >
                  {isSaving ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      {t("recording")}...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check me-2"></i>
                      {t("Save_deduction")}
                    </>
                  )}
                </Button>
                {!canSave && !isSaving && (
                  <small className="form-text text-muted mt-1">
                    {!selectedDeduction && "Select a deduction"}
                    {selectedDeduction && !startDate && "Enter a start date"}
                    {selectedDeduction && startDate && !areDatesValid && "Check dates"}
                  </small>
                )}
              </Col>
            </Row>
          </>
        )}
      </CardBody>
    </div>
  );
};
export default withRouter(withTranslation()(DeductionForm));
